import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';

// Helper function to get status name
const getStatusName = (status) => {
return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to get stock level name
const getStockLevelName = (balance) => {
if (balance <= 0) {
    return 'Out of Stock';
} else if (balance <= 10) {
    return 'Low';
} else if (balance <= 50) {
    return 'Normal';
} else {
    return 'High';
}
};

// Helper function to format currency
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(amount) + " "+"sh";
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
if (!text) return [];
const words = text.toString().split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Helper function to get branch name
const getBranchName = (branchId, branchNamesData) => {
if (!branchNamesData || !Array.isArray(branchNamesData)) return 'N/A';
const branch = branchNamesData.find(b => {
    // Handle both string and number comparison
    const branchIdNum = parseInt(branchId);
    const bIdNum = parseInt(b.id);
    return bIdNum === branchIdNum;
});
return branch ? branch.name : 'N/A';
};

// Manual table drawing function for PDF - Landscape mode with selected columns
const drawStocksTableLandscape = (doc, data, startY, branchNamesFilterData) => {
const pageWidth = doc.internal.pageSize.getWidth(); // ~297mm in landscape
const pageHeight = doc.internal.pageSize.getHeight(); // ~210mm in landscape
const margin = 20;
const rowHeight = 12;

// Column widths for selected columns in landscape mode
const colWidths = [15, 25, 50, 20, 20, 20, 25, 25]; // #, Stock Date, Item Name, Balance, Cost, Selling, Stock Level, Branch

let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;

// Check if we need a new page
if (currentY + neededHeight > pageHeight - 30) {
    doc.addPage('l');
    currentY = margin;
}

// Draw table header
doc.setFillColor(25, 118, 210); // Blue header
doc.setTextColor(255, 255, 255); // White text
doc.setFont(undefined, 'bold');

const headers = ['#', 'Stock Date', 'Item Name', 'Balance', 'Cost', 'Selling', 'Stock Level', 'Branch'];
let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

// Draw header underline
doc.setDrawColor(25, 118, 210);
doc.setLineWidth(0.5);
doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > pageHeight - 30) {
    doc.addPage('l');
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    doc.setDrawColor(25, 118, 210);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Draw vertical lines
    let verticalX = margin;
    for (let i = 0; i <= colWidths.length; i++) {
    doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
    if (i < colWidths.length) verticalX += colWidths[i];
    }
    
    // Draw horizontal lines
    doc.line(margin, currentY, pageWidth - margin, currentY);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    // Color code stock level column (index 6)
    if (cellIndex === 6) {
        const level = lines[0];
        if (level === 'Out of Stock') {
        doc.setTextColor(244, 67, 54);
        } else if (level === 'Low') {
        doc.setTextColor(255, 152, 0);
        } else if (level === 'Normal') {
        doc.setTextColor(76, 175, 80);
        } else {
        doc.setTextColor(33, 150, 243);
        }
    }
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Excel export for Stocks - All attributes
export const exportToExcel = (stocks, branchNamesFilterData, filters = {}) => {
try {
    // Prepare data for Excel with all attributes
    const excelData = stocks.map((stock, index) => {
    return {
        'No.': index + 1,
        'Stock Date': new Date(stock.stock_date).toLocaleDateString(),
        'Item Name': stock.name,
        'Quantity': stock.quantity,
        'Balance': stock.balance,
        'UOM': stock.uom,
        'Cost': formatCurrency(stock.cost),
        'Total Cost': formatCurrency(stock.total_cost),
        'Selling': formatCurrency(stock.selling),
        'Total Selling': formatCurrency(stock.total_selling),
        'Profit': formatCurrency((stock.selling - stock.cost) * stock.balance),
        'Stock Level': getStockLevelName(stock.balance),
        'Status': getStatusName(stock.status),
        'Branch': getBranchName(stock.branch_id, branchNamesFilterData),
        'Description': stock.description || '',
        'Notes': stock.notes || '',
        'Created Date': new Date(stock.created_at).toLocaleDateString(),
        'Updated Date': new Date(stock.updated_at).toLocaleDateString()
    };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
    Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
        widths[idx] = length;
        }
    });
    return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stocks');

    // Generate Excel file and download
    const fileName = `stocks_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Stocks Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Stocks - Selected columns only, Landscape mode
export const exportToPDF = async (stocks, branchNamesFilterData, filters = {}) => {
return new Promise((resolve, reject) => {
    try {
    
    // Create PDF in landscape mode
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let currentY = margin;
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('STOCK ITEMS REPORT', pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, currentY);
    currentY += 8;
    doc.text(`Total Items: ${stocks.length}`, margin, currentY);
    currentY += 8;
    
    // Filters applied
    const filterTexts = [];
    if (filters.stock_level) filterTexts.push(`Stock Level: ${filters.stock_level}`);
    if (filters.status) filterTexts.push(`Status: ${getStatusName(filters.status)}`);
    if (filters.dateFrom) filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
    if (filters.dateTo) filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
    
    if (filterTexts.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const filtersText = `Filters: ${filterTexts.join(', ')}`;
        const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
        
        filtersLines.forEach(line => {
        if (currentY > pageHeight - 40) {
            doc.addPage('l');
            currentY = margin;
        }
        doc.text(line, margin, currentY);
        currentY += 6;
        });
        
        currentY += 10;
    }
    
    // Prepare table data with selected columns only
    const tableData = stocks.map((stock, index) => {
        return [
        (index + 1).toString(),
        new Date(stock.stock_date).toLocaleDateString(),
        stock.name,
        stock.balance.toString(),
        formatCurrency(stock.cost),
        formatCurrency(stock.selling),
        getStockLevelName(stock.balance),
        getBranchName(stock.branch_id, branchNamesFilterData)
        ];
    });
    
    // Draw the table
    drawStocksTableLandscape(doc, tableData, currentY + 10, branchNamesFilterData);
    
    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }
    
    const fileName = `stocks_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    resolve({ success: true, fileName });
    } catch (error) {
    console.error('Stocks PDF export failed:', error);
    reject(new Error('Failed to export to PDF: ' + error.message));
    }
});
};

// CSV export for Stocks - All attributes
export const exportToCSV = (stocks, branchNamesFilterData) => {
try {
    
    const headers = [
    'No.', 'Stock Date', 'Item Name', 'Quantity', 'Balance', 'UOM', 
    'Cost', 'Total Cost', 'Selling', 'Total Selling', 'Profit', 
    'Stock Level', 'Status', 'Branch', 'Description', 'Notes', 
    'Created Date', 'Updated Date'
    ];
    
    const csvData = stocks.map((stock, index) => {
    const stockLevel = getStockLevelName(stock.balance);
    const profit = (stock.selling - stock.cost) * stock.balance;
    
    return [
        index + 1,
        `"${new Date(stock.stock_date).toLocaleDateString().replace(/"/g, '""')}"`,
        `"${stock.name.replace(/"/g, '""')}"`,
        stock.quantity,
        stock.balance,
        `"${stock.uom.replace(/"/g, '""')}"`,
        formatCurrency(stock.cost),
        formatCurrency(stock.total_cost),
        formatCurrency(stock.selling),
        formatCurrency(stock.total_selling),
        formatCurrency(profit),
        `"${stockLevel.replace(/"/g, '""')}"`,
        getStatusName(stock.status),
        `"${getBranchName(stock.branch_id, branchNamesFilterData).replace(/"/g, '""')}"`,
        `"${(stock.description || '').replace(/"/g, '""')}"`,
        `"${(stock.notes || '').replace(/"/g, '""')}"`,
        `"${new Date(stock.created_at).toLocaleDateString().replace(/"/g, '""')}"`,
        `"${new Date(stock.updated_at).toLocaleDateString().replace(/"/g, '""')}"`
    ];
    });

    const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stocks_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Stocks CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};